import { useCallback } from 'react';

import { useAppDispatch } from 'Libs/redux/store';

import { actions } from 'Pages/Courses/redux/slice';
import { AI_COURSE_VIEWS } from 'Pages/Courses/Edition/views';
import { AILoadingSates } from 'Pages/Courses/Edition/views/AILoading/AILoading';
import { CoursesState, useModuleSelector } from 'Pages/Courses/redux';

export const useAICourseView = () => {
  const dispatch = useAppDispatch();

  const viewType = useModuleSelector((state) => state.aiEdition.viewType) as AI_COURSE_VIEWS;
  const setViewType = (newViewType: AI_COURSE_VIEWS) => dispatch(actions.setAiViewType(newViewType));

  const setAIEdition = (newAIEditionState: Partial<CoursesState['aiEdition']>) => {
    dispatch(actions.setAIEditionState(newAIEditionState));
  };

  const prompt = useModuleSelector((state) => state.aiEdition.prompt);
  const requestId = useModuleSelector((state) => state.aiEdition.requestId);

  /**
   * Set the correct view when entering the course edition page according to
   * the course generation state
   */
  const initialiseAICourseView = useCallback(
    (course: CoursesState['edition']['course'], loadingState: AILoadingSates) => {
      const viewType = getViewTypeFromCourseGenerationState(course);
      if (loadingState === 'IS_LOADING' || loadingState === 'IS_FINISHED') return;
      setViewType(viewType);
    },
    [setViewType],
  );

  return {
    prompt,
    requestId,
    viewType,
    initialiseAICourseView,
    setAIEdition,
    setViewType,
  };
};

const getViewTypeFromCourseGenerationState = (course: CoursesState['edition']['course']) => {
  if (!course) {
    return AI_COURSE_VIEWS.INITIAL;
  }
  if (course.aiGenerationStatus === 'INITIALIZED') return AI_COURSE_VIEWS.INITIAL;
  if (course.aiGenerationStatus === 'PLAN_GENERATED') return AI_COURSE_VIEWS.PLAN_VALIDATION;
  if (course.aiGenerationStatus === 'MICROKNOWLEDGES_GENERATED') return AI_COURSE_VIEWS.MICROKNOWLEDGES_VALIDATION;
  if (course.aiGenerationStatus === 'COMPLETED') return AI_COURSE_VIEWS.MICROKNOWLEDGES_VALIDATION;
  if (course.aiGenerationStatus === 'PREVIOUSLY_CREATED') return AI_COURSE_VIEWS.MICROKNOWLEDGES_VALIDATION;
  if (course.aiGenerationStatus === 'MANUAL') return AI_COURSE_VIEWS.MICROKNOWLEDGES_VALIDATION;

  return AI_COURSE_VIEWS.INITIAL;
};

export default useAICourseView;
